// src/pages/Extras/AgentPublicProfile.jsx
// Updated to use custom CSS classes and design system

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

const AgentPublicProfile = () => {
  const { agentId } = useParams();
  const navigate = useNavigate();
  
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('about');
  const [showContactModal, setShowContactModal] = useState(false);

  useEffect(() => {
    loadAgentProfile();
  }, [agentId]);

  const loadAgentProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`https://app.akiya2.com/agents/get-agent-by-id/${agentId}`);
      
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Agent not found');
        }
        throw new Error('Failed to load agent profile');
      }
      
      const data = await response.json();
      setAgent(data);
    } catch (error) {
      console.error('Failed to load agent:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const formatExperience = (years) => {
    if (!years) return 'New to the industry';
    return years === 1 ? '1 year of experience' : `${years} years of experience`;
  };

  const formatJoinDate = (dateString) => {
    if (!dateString) return 'Recently joined';
    const date = new Date(dateString);
    return `Joined ${date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`;
  };

  const getVerificationBadge = () => {
    if (!agent?.is_verified) return null;
    
    return (
      <div className="inline-flex items-center px-3 py-1 badge-verified rounded-full">
        <i className="fas fa-certificate mr-2"></i>
        <span className="text-sm font-medium">Verified Agent</span>
      </div>
    );
  };

  const getSubscriptionBadge = () => {
    if (!agent?.subscription_type || agent.subscription_type === 'basic') return null;
    
    return (
      <div className="inline-flex items-center px-3 py-1 badge-premium rounded-full">
        <i className="fas fa-crown mr-2"></i>
        <span className="text-sm font-medium">Premium Agent</span>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--bg-grey)' }}>
        <div className="flex flex-col items-center space-y-4">
          <div className="w-16 h-16 rounded-full flex items-center justify-center animate-pulse gradient-primary">
            <i className="fas fa-user-tie text-white text-2xl"></i>
          </div>
          <p className="light-header">Loading agent profile...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--bg-grey)' }}>
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg border-primary border p-8 text-center">
          <div className="w-16 h-16 teal-bg rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="fas fa-exclamation-triangle text-primary text-2xl"></i>
          </div>
          <h2 className="dark-header mb-2" style={{ color: 'var(--color-2)' }}>Agent Not Found</h2>
          <p className="light-header mb-6">{error}</p>
          <button
            onClick={() => navigate('/listings')}
            className="gradient-primary text-white px-6 py-3 rounded-lg hover:opacity-90 transition-colors non-selectable"
          >
            Browse All Listings
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bg-grey)' }}>
      {/* FontAwesome CDN */}
      <link 
        rel="stylesheet" 
        href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css" 
      />

      {/* Custom Styles */}
      <style>{`
.gradient-primary {
  background-color: var(--medium-sea-green);
}
.gradient-primary:hover {
  background-color: var(--sea-green);
}
.gradient-cover {
  background-color: var(--sea-green);
}
.text-primary {
  color: var(--medium-sea-green);
}
.text-secondary {
  color: var(--dark-grey);
}
.border-primary {
  border-color: rgba(16, 201, 192, 0.2);
}
.teal-bg {
  background-color: rgba(14, 177, 169, 0.05);
}
.teal-hover:hover {
  background-color: rgba(14, 177, 169, 0.1);
}
.badge-verified {
  background-color: rgba(14, 177, 169, 0.1);
  border: 1px solid rgba(14, 177, 169, 0.2);
  color: var(--sea-green);
}
.badge-premium {
  background-color: var(--medium-sea-green);
  color: white;
}
.tab-active {
  border-color: var(--medium-sea-green);
  color: var(--medium-sea-green);
}
.tab-inactive {
  border-color: transparent;
  color: var(--dark-grey);
}
.tab-inactive:hover {
  color: var(--color-2);
  border-color: rgba(148, 153, 149, 0.3);
}
.contact-item:hover {
  background-color: rgba(14, 177, 169, 0.05);
}
.specialization-card {
  background-color: rgba(16, 201, 192, 0.1);
  border: 1px solid rgba(16, 201, 192, 0.2);
}
.area-card {
  background-color: rgba(16, 201, 192, 0.05);
  border: 1px solid rgba(16, 201, 192, 0.15);
}
.property-card:hover {
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
}
.property-card:hover .property-title {
  color: var(--medium-sea-green);
}

      `}</style>

      {/* Cover & Profile Section */}
      <div className="relative">
        {/* Cover Photo */}
        <div className="h-80  relative overflow-hidden">
          <div className="absolute inset-0 bg-[var(--medium-sea-green)]"></div>
          <div className="absolute inset-0 "></div>

          {/* Cover Content */}
          <div className="absolute bottom-0 left-0 right-0 p-8">
            <div className="f-section mx-auto" style={{ padding: '0 clamp(6px, 4vw, 30px)' }}>
              <div className="flex flex-col md:flex-row items-end space-y-4 md:space-y-0 md:space-x-6">
                {/* Profile Image */}
                <div className="relative">
                  <div className="w-32 h-32 bg-white rounded-full p-2 shadow-xl">
                  {console.log("agent.image_url")}

                    {console.log(agent.image_url)}
                    {agent.image_url ? (
                      
                      <img
                        src={agent.image_url}
                        alt={agent.name}
                        className="w-full h-full rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full gradient-primary rounded-full flex items-center justify-center">
                        <i className="fas fa-user-tie text-white text-3xl"></i>
                      </div>
                    )}
                  </div>

                  {/* Online Indicator */}
                  <div className="absolute bottom-2 right-2 w-6 h-6 bg-green-500 border-4 border-white rounded-full"></div>
                </div>

                <div className="flex flex-1 flex-col md:flex-row md:items-end md:justify-between">
                  {/* Name & Title */}
                  <div className="text-white text-center md:text-left">
  <h1 className={`dark-header text-white ${agent.agent_type === 'company' ? 'mb-5' : 'mb-2'}`}>
    {agent.name}
  </h1>
  {agent.agent_type !== 'company' && (
    <p className="dark-header text-white mb-3">Real Estate Professional</p>
  )}
  <div className="flex flex-wrap gap-3 justify-center md:justify-start">
    {getVerificationBadge()}
    {getSubscriptionBadge()}
  </div>
</div>

                  <div className="mt-4 md:mt-0">
                    <button
                      onClick={() => setShowContactModal(true)}
                      className="bg-white/20 backdrop-blur text-white px-6 py-3 rounded-lg hover:bg-white/30 transition-colors border border-white/30 non-selectable"
                    >
                      <i className="fas fa-envelope mr-2"></i>
                      Contact Agent
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Stats Bar */}
        <div className="bg-white border-b border-primary shadow-sm">
          <div className="f-section mx-auto py-4" style={{ padding: '16px clamp(6px, 4vw, 30px)' }}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold" style={{ color: 'var(--color-2)' }}>{agent.statistics?.total_listings || 0}</div>
                <div className="text-sm text-secondary">Total Listings</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-primary">{agent.statistics?.active_listings || 0}</div>
                <div className="text-sm text-secondary">Active Listings</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-primary">{agent.years_experience || 0}</div>
                <div className="text-sm text-secondary">Years Experience</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-primary">{agent.statistics?.featured_listings || 0}</div>
                <div className="text-sm text-secondary">Featured</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="f-section mx-auto py-8" style={{ padding: '32px clamp(6px, 4vw, 30px)' }}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            {/* Contact Information */}
            <div className="bg-white rounded-2xl shadow-sm border-primary border p-6">
              <h3 className="dark-header-nospacing mb-4" style={{ color: 'var(--color-2)', margin: 0, marginBottom: '16px' }}>Contact Information</h3>
              
              <div className="space-y-4">
                {agent.phone && (
                  <div className="flex items-center">
                    <div className="w-10 h-10 teal-bg rounded-lg flex items-center justify-center mr-3">
                      <i className="fas fa-phone text-primary"></i>
                    </div>
                    <div>
                      <p className="text-sm text-secondary">Phone</p>
                      <a href={`tel:${agent.phone}`} className="hover:text-primary transition-colors" style={{ color: 'var(--color-2)' }}>
                        {agent.phone}
                      </a>
                    </div>
                  </div>
                )}

                {agent.email && (
                  <div className="flex items-center">
                    <div className="w-10 h-10 teal-bg rounded-lg flex items-center justify-center mr-3">
                      <i className="fas fa-envelope text-primary"></i>
                    </div>
                    <div>
                      <p className="text-sm text-secondary">Email</p>
                      <a href={`mailto:${agent.email}`} className="hover:text-primary transition-colors break-all" style={{ color: 'var(--color-2)' }}>
                        {agent.email}
                      </a>
                    </div>
                  </div>
                )}

                {agent.whatsapp_number && (
                  <div className="flex items-center">
                    <div className="w-10 h-10 teal-bg rounded-lg flex items-center justify-center mr-3">
                      <i className="fab fa-whatsapp text-primary"></i>
                    </div>
                    <div>
                      <p className="text-sm text-secondary">WhatsApp</p>
                      <a href={`https://wa.me/${agent.whatsapp_number.replace(/[^\d]/g, '')}`} className="hover:text-primary transition-colors" style={{ color: 'var(--color-2)' }}>
                        {agent.whatsapp_number}
                      </a>
                    </div>
                  </div>
                )}

                {agent.website && (
                  <div className="flex items-center">
                    <div className="w-10 h-10 teal-bg rounded-lg flex items-center justify-center mr-3">
                      <i className="fas fa-globe text-primary"></i>
                    </div>
                    <div>
                      <p className="text-sm text-secondary">Website</p>
                      <a href={agent.website} target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors" style={{ color: 'var(--color-2)' }}>
                        Visit Website
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Business Information */}
            {(agent.business_address || agent.office_hours || agent.license_number) && (
              <div className="bg-white rounded-2xl shadow-sm border-primary border p-6">
                <h3 className="dark-header-nospacing mb-4" style={{ color: 'var(--color-2)', margin: 0, marginBottom: '16px' }}>Business Details</h3>
                
                <div className="space-y-4">
                  {agent.business_address && (
                    <div>
                      <p className="text-sm text-secondary mb-1">Office Address</p>
                      <p style={{ color: 'var(--color-2)' }}>{agent.business_address}</p>
                    </div>
                  )}

                  {agent.office_hours && (
                    <div>
                      <p className="text-sm text-secondary mb-1">Office Hours</p>
                      <p style={{ color: 'var(--color-2)' }}>{agent.office_hours}</p>
                    </div>
                  )}

                  {agent.license_number && (
                    <div>
                      <p className="text-sm text-secondary mb-1">License Number</p>
                      <p className="font-mono text-sm" style={{ color: 'var(--color-2)' }}>{agent.license_number}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Professional Info */}
            <div className="bg-white rounded-2xl shadow-sm border-primary border p-6">
              <h3 className="dark-header-nospacing mb-4" style={{ color: 'var(--color-2)', margin: 0, marginBottom: '16px' }}>Professional Info</h3>
              
              <div className="space-y-3">
                <div className="flex items-center">
                  <i className="fas fa-calendar-alt text-secondary mr-3"></i>
                  <span style={{ color: 'var(--color-2)' }}>{formatExperience(agent.years_experience)}</span>
                </div>
                
                <div className="flex items-center">
                  <i className="fas fa-user-clock text-secondary mr-3"></i>
                  <span style={{ color: 'var(--color-2)' }}>{formatJoinDate(agent.created_at)}</span>
                </div>

                {agent.agent_type && (
                  <div className="flex items-center">
                    <i className="fas fa-briefcase text-secondary mr-3"></i>
                    <span className="capitalize" style={{ color: 'var(--color-2)' }}>{agent.agent_type} Agent</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="lg:col-span-2 space-y-6">
            {/* Navigation Tabs */}
            <div className="bg-white rounded-2xl shadow-sm border-primary border">
              <div className="border-b border-primary">
                <nav className="flex space-x-8" style={{ padding: '0 clamp(6px, 4vw, 24px)' }}>
                  {[
                    { id: 'about', label: 'About', icon: 'fas fa-user' },
                    { id: 'expertise', label: 'Expertise', icon: 'fas fa-star' },
                    { id: 'listings', label: 'Properties', icon: 'fas fa-home' }
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center py-4 px-2 border-b-2 font-medium text-sm transition-all duration-300 non-selectable ${
                        activeTab === tab.id ? 'tab-active' : 'tab-inactive'
                      }`}
                    >
                      <i className={`${tab.icon} mr-2`}></i>
                      {tab.label}
                    </button>
                  ))}
                </nav>
              </div>

              {/* Tab Content */}
              <div className="p-6">
                {activeTab === 'about' && (
                  <div className="space-y-6">
                    {/* Bio Section */}
                    {agent.bio && (
                      <div>
                        <h4 className="dark-header mb-3" style={{ color: 'var(--color-2)', fontSize: '18px' }}>About Me</h4>
                        <p className="light-header leading-relaxed">{agent.bio}</p>
                      </div>
                    )}

                    {/* Professional Summary */}
                    {agent.professional_summary && (
                      <div>
                        <h4 className="dark-header mb-3" style={{ color: 'var(--color-2)', fontSize: '18px' }}>Professional Summary</h4>
                        <p className="light-header leading-relaxed">{agent.professional_summary}</p>
                      </div>
                    )}

                    {/* Languages */}
                    {agent.languages_spoken && agent.languages_spoken.length > 0 && (
                      <div>
                        <h4 className="dark-header mb-3" style={{ color: 'var(--color-2)', fontSize: '18px' }}>Languages Spoken</h4>
                        <div className="flex flex-wrap gap-2">
                          {agent.languages_spoken.map((language, index) => (
                            <span
                              key={index}
                              className="px-3 py-1 badge-verified rounded-full text-sm"
                            >
                              {language}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Certifications */}
                    {agent.certifications && agent.certifications.length > 0 && (
                      <div>
                        <h4 className="dark-header mb-3" style={{ color: 'var(--color-2)', fontSize: '18px' }}>Certifications</h4>
                        <div className="space-y-2">
                          {agent.certifications.map((cert, index) => (
                            <div key={index} className="flex items-center">
                              <i className="fas fa-certificate text-primary mr-3"></i>
                              <span style={{ color: 'var(--color-2)' }}>{cert}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'expertise' && (
                  <div className="space-y-6">
                    {/* Specializations */}
                    {agent.specializations && agent.specializations.length > 0 && (
                      <div>
                        <h4 className="dark-header mb-3" style={{ color: 'var(--color-2)', fontSize: '18px' }}>Specializations</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {agent.specializations.map((spec, index) => (
                            <div
                              key={index}
                              className="flex items-center p-3 specialization-card rounded-lg"
                            >
                              <i className="fas fa-check-circle text-primary mr-3"></i>
                              <span className="font-medium capitalize" style={{ color: 'var(--color-2)' }}>{spec}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Service Areas */}
                    {agent.service_areas && agent.service_areas.length > 0 && (
                      <div>
                        <h4 className="dark-header mb-3" style={{ color: 'var(--color-2)', fontSize: '18px' }}>Service Areas</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {agent.service_areas.map((area, index) => (
                            <div
                              key={index}
                              className="flex items-center p-3 area-card rounded-lg"
                            >
                              <i className="fas fa-map-marker-alt text-primary mr-3"></i>
                              <span className="font-medium" style={{ color: 'var(--color-2)' }}>{area}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'listings' && (
                  <AgentListings agentId={agent.agent_id} />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Modal */}
      {showContactModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-primary">
              <div className="flex items-center justify-between">
                <h3 className="dark-header" style={{ color: 'var(--color-2)', margin: 0 }}>Contact {agent.name}</h3>
                <button
                  onClick={() => setShowContactModal(false)}
                  className="text-secondary hover:opacity-70 transition-colors non-selectable"
                >
                  <i className="fas fa-times text-xl"></i>
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              {agent.phone && (
                <a
                  href={`tel:${agent.phone}`}
                  className="flex items-center p-4 teal-bg rounded-lg contact-item transition-colors"
                >
                  <i className="fas fa-phone text-primary mr-4"></i>
                  <div>
                    <p className="font-medium" style={{ color: 'var(--color-2)' }}>Call Now</p>
                    <p className="text-primary">{agent.phone}</p>
                  </div>
                </a>
              )}

              {agent.email && (
                <a
                  href={`mailto:${agent.email}`}
                  className="flex items-center p-4 teal-bg rounded-lg contact-item transition-colors"
                >
                  <i className="fas fa-envelope text-primary mr-4"></i>
                  <div>
                    <p className="font-medium" style={{ color: 'var(--color-2)' }}>Send Email</p>
                    <p className="text-primary text-sm break-all">{agent.email}</p>
                  </div>
                </a>
              )}

              {agent.whatsapp_number && (
                <a
                  href={`https://wa.me/${agent.whatsapp_number.replace(/[^\d]/g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center p-4 teal-bg rounded-lg contact-item transition-colors"
                >
                  <i className="fab fa-whatsapp text-primary mr-4"></i>
                  <div>
                    <p className="font-medium" style={{ color: 'var(--color-2)' }}>WhatsApp</p>
                    <p className="text-primary">{agent.whatsapp_number}</p>
                  </div>
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Agent Listings Component - UPDATED to use design system
const AgentListings = ({ agentId }) => {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadAgentListings();
  }, [agentId]);

  const loadAgentListings = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Loading listings for agent:', agentId);
      
      // First, get the agent's details to fetch their property IDs
      const agentResponse = await fetch(`https://app.akiya2.com/agents/get-agent-by-id/${agentId}`);
      
      if (!agentResponse.ok) {
        throw new Error(`Failed to load agent details: ${agentResponse.status}`);
      }
      
      const agentData = await agentResponse.json();
      console.log('Agent data:', agentData);
      
      // Get property IDs from agent data - check multiple possible field names
      let propertyIds = [];
      if (agentData.listing_ids && Array.isArray(agentData.listing_ids)) {
        propertyIds = agentData.listing_ids;
      } else if (agentData.property_ids && Array.isArray(agentData.property_ids)) {
        propertyIds = agentData.property_ids;
      } else if (agentData.listings && Array.isArray(agentData.listings)) {
        propertyIds = agentData.listings;
      } else if (agentData.properties && Array.isArray(agentData.properties)) {
        propertyIds = agentData.properties;
      }
      
      console.log('Agent property IDs:', propertyIds);
      
      if (!propertyIds || propertyIds.length === 0) {
        console.log('No property IDs found for agent');
        setListings([]);
        return;
      }
      
      // Now fetch the specific listings using the property IDs
      const listingsResponse = await fetch('https://app.akiya2.com/listings_by_ids', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          property_ids: propertyIds
        })
      });
      
      if (!listingsResponse.ok) {
        // If the bulk fetch endpoint doesn't exist, fall back to individual fetches
        console.log('Bulk fetch failed, trying individual fetches...');
        await loadListingsIndividually(propertyIds);
        return;
      }
      
      const listingsData = await listingsResponse.json();
      console.log('Fetched listings data:', listingsData);
      
      // Extract listings from response
      let allListings = [];
      if (Array.isArray(listingsData)) {
        allListings = listingsData;
      } else if (listingsData.listings && Array.isArray(listingsData.listings)) {
        allListings = listingsData.listings;
      } else if (listingsData.data && Array.isArray(listingsData.data)) {
        allListings = listingsData.data;
      }
      
      // Filter only approved listings
      const approvedListings = allListings.filter(listing => {
        const isApproved = listing.status === 'approved' || 
                          listing.approval_status === 'approved' ||
                          (!listing.underCheck && listing.status !== 'pending_approval' && listing.approval_status !== 'pending');
        
        console.log(`Listing ${listing.property_id}: status=${listing.status}, approval_status=${listing.approval_status}, underCheck=${listing.underCheck}, isApproved=${isApproved}`);
        
        return isApproved;
      });
      
      console.log('Approved listings:', approvedListings);
      setListings(approvedListings);
      
    } catch (error) {
      console.error('Failed to load agent listings:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Fallback method if bulk fetch API doesn't exist
  const loadListingsIndividually = async (propertyIds) => {
    try {
      console.log('Loading listings individually for IDs:', propertyIds);
      
      // Fetch from public approved listings and filter
      const response = await fetch('https://app.akiya2.com/public/approved-listings');
      
      if (!response.ok) {
        throw new Error(`Failed to load approved listings: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Extract all listings
      let allListings = [];
      if (Array.isArray(data)) {
        allListings = data;
      } else if (data.listings && Array.isArray(data.listings)) {
        allListings = data.listings;
      } else if (data.data && Array.isArray(data.data)) {
        allListings = data.data;
      }
      
      // Filter listings that match the agent's property IDs
      const agentListings = allListings.filter(listing => {
        const propertyId = listing.property_id || listing.Property_ID;
        return propertyIds.includes(propertyId);
      });
      
      console.log('Filtered agent listings from approved:', agentListings);
      setListings(agentListings);
      
    } catch (fallbackError) {
      console.error('Fallback method also failed:', fallbackError);
      throw fallbackError;
    }
  };

  const formatPrice = (price) => {
    if (!price) return 'Contact for Price';
    const numericPrice = price.toString().replace(/[^\d]/g, '');
    if (!numericPrice) return price;
    return `¥${parseInt(numericPrice).toLocaleString()}`;
  };

  const getPropertyImage = (listing) => {
    // Check multiple possible image field names - handle both cases
    if ((listing.Images || listing.images) && (listing.Images || listing.images).length > 0) {
      return (listing.Images || listing.images)[0];
    }
    if (listing.image) {
      return listing.image;
    }
    // Return a placeholder
    return 'https://via.placeholder.com/400x300/e5e7eb/6b7280?text=Property+Image';
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="w-16 h-16 teal-bg rounded-full flex items-center justify-center mx-auto mb-4">
          <i className="fas fa-spinner fa-spin text-primary text-2xl"></i>
        </div>
        <p className="light-header">Loading properties...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <div className="w-16 h-16 teal-bg rounded-full flex items-center justify-center mx-auto mb-4">
          <i className="fas fa-exclamation-triangle text-primary text-2xl"></i>
        </div>
        <p className="light-header text-primary mb-4">Failed to load listings: {error}</p>
        <button
          onClick={loadAgentListings}
          className="text-primary hover:opacity-70 font-medium non-selectable"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (listings.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="w-20 h-20 teal-bg rounded-full flex items-center justify-center mx-auto mb-6">
          <i className="fas fa-home text-primary text-3xl opacity-50"></i>
        </div>
        <h4 className=" mb-2" style={{ color: 'var(--color-2)' }}>No Approved Properties</h4>
        <p className="light-header">This agent doesn't have any approved properties yet.</p>
        <p className="text-sm text-secondary mt-2">Agent ID: {agentId}</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h4 className="dark-header" style={{ color: 'var(--color-2)', margin: 0, fontSize: '18px' }}>
          Approved Properties ({listings.length})
        </h4>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {listings.map((listing, index) => (
          <div
            key={listing.property_id || listing.Property_ID || index}
            className="bg-white rounded-xl shadow-sm border-primary border overflow-hidden property-card transition-all duration-300 group cursor-pointer"
            onClick={() => {
              // Open property details in new tab if you have a property details page
              const propertyId = listing.property_id || listing.Property_ID;
              if (propertyId) {
                window.open(`/individual-akiya/${propertyId}`, '_blank');
              }
            }}
          >
            {/* Property Image */}
            <div className="relative h-48 overflow-hidden">
              <img
                src={getPropertyImage(listing)}
                alt={listing.Address || listing.address || 'Property'}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                onError={(e) => {
                  e.target.src = 'https://via.placeholder.com/400x300/e5e7eb/6b7280?text=Property+Image';
                }}
              />
              
              {/* Price Badge */}
              <div className="absolute top-3 left-3 gradient-primary text-white px-3 py-1 rounded-full">
                <span className="text-sm font-bold">{formatPrice(listing.Price || listing.price)}</span>
              </div>

              {/* Status Badge */}
              {(listing.Empty_or_Not || listing.status) && (
                <div className="absolute top-3 right-3 bg-white px-2 py-1 rounded-full text-xs font-medium" style={{ color: 'var(--color-2)' }}>
                  {listing.Empty_or_Not || listing.status}
                </div>
              )}

              {/* Featured Badge */}
              {listing.featured && (
                <div className="absolute bottom-3 left-3 badge-premium text-white px-2 py-1 rounded-full text-xs font-medium">
                  <i className="fas fa-star mr-1"></i>
                  Featured
                </div>
              )}
            </div>

            {/* Property Details */}
            <div className="p-4">
              <div className="mb-3">
                <h5 className="font-semibold mb-1 line-clamp-2 property-title transition-colors" style={{ color: 'var(--color-2)' }}>
                  {listing.Address || listing.address || 'Property Address'}
                </h5>
                <p className="text-sm text-secondary">
                  {(listing.Municipality || listing.municipality || 'Location')} • {(listing.Prefecture || listing.prefecture || 'Prefecture')}
                </p>
              </div>

              {/* Property Features */}
              <div className="grid grid-cols-2 gap-3 mb-4 text-xs">
                {(listing.Room_Type || listing.room_type) && (
                  <div className="flex items-center text-secondary">
                    <i className="fas fa-home mr-1 w-3"></i>
                    <span className="truncate">{listing.Room_Type || listing.room_type}</span>
                  </div>
                )}
                
                {(listing.Year_of_Construction || listing.year_of_construction) && (
                  <div className="flex items-center text-secondary">
                    <i className="fas fa-calendar mr-1 w-3"></i>
                    <span>{listing.Year_of_Construction || listing.year_of_construction}</span>
                  </div>
                )}
                
                {(listing.Area_of_House || listing.area_of_house) && (
                  <div className="flex items-center text-secondary">
                    <i className="fas fa-expand-arrows-alt mr-1 w-3"></i>
                    <span className="truncate">{listing.Area_of_House || listing.area_of_house}</span>
                  </div>
                )}
                
                {(listing.No_of_Floors || listing.no_of_floors) && (
                  <div className="flex items-center text-secondary">
                    <i className="fas fa-layer-group mr-1 w-3"></i>
                    <span>{listing.No_of_Floors || listing.no_of_floors} floors</span>
                  </div>
                )}
              </div>

              {/* Description */}
              {(listing.description || listing.Description) && (
                <p className="text-sm text-secondary mb-3 line-clamp-2">
                  {listing.description || listing.Description}
                </p>
              )}

              {/* Call to Action */}
              <div className="flex items-center justify-between pt-3 border-t border-primary">
                <span className="text-xs text-secondary">
                  Property ID: {listing.property_id || listing.Property_ID || 'N/A'}
                </span>
                <div className="flex items-center text-primary text-xs group-hover:opacity-70 transition-colors">
                  <span className="mr-1">View Details</span>
                  <i className="fas fa-arrow-right"></i>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Load More Button (if you want to implement pagination) */}
      {listings.length >= 6 && (
        <div className="text-center mt-8">
          <p className="light-header text-sm">
            Showing {listings.length} properties by this agent
          </p>
        </div>
      )}
    </div>
  );
};

export default AgentPublicProfile;